import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/components/context/AuthContext';
import { sanitizeSearchInput } from '@/utils/security';
import type { 
  SupportRequest, 
  SupportTemplate, 
  SupportCategory,
  SupportStatus,
  SupportPriority 
} from '@/types/support';
import {
  SUPPORT_QUERY_KEYS,
  SUPPORT_ERROR_MESSAGES,
  SUPPORT_SUCCESS_MESSAGES,
  SUPPORT_RETRY_CONFIG,
  SUPPORT_PAGINATION
} from '@/constants/support';

interface PaginationOptions {
  page?: number;
  pageSize?: number;
  sortBy?: keyof SupportRequest;
  sortOrder?: 'asc' | 'desc';
}

interface FilterOptions {
  status?: SupportStatus;
  priority?: SupportPriority;
  category?: SupportCategory;
  searchTerm?: string;
}

interface CreateRequestData {
  category: SupportCategory;
  priority: SupportPriority;
  subject: string;
  description: string;
  screenshots?: string[];
  metadata?: Record<string, any>;
}

interface UpdateRequestData {
  status?: SupportStatus;
  priority?: SupportPriority;
  assignedTo?: string;
  metadata?: Record<string, any>;
}

interface CommentData {
  requestId: string;
  content: string;
  isInternal?: boolean;
}

export function useSchoolSupport() {
  const { user, userSchool } = useAuth();
  const queryClient = useQueryClient();
  const [retryCount, setRetryCount] = useState(0);

  // Helper function to handle errors with retry logic
  const handleError = useCallback((error: any, operation: string) => {
    console.error(`Support operation failed: ${operation}`, error);
    
    // Check if we should retry
    if (retryCount < SUPPORT_RETRY_CONFIG.maxRetries && SUPPORT_RETRY_CONFIG.retryCondition(error)) {
      setRetryCount(prev => prev + 1);
      const delay = SUPPORT_RETRY_CONFIG.retryDelay(retryCount);
      
      toast({
        title: 'Connection Issue',
        description: `Retrying in ${delay / 1000} seconds...`,
        variant: 'default'
      });
      
      return setTimeout(() => {
        // Trigger retry by invalidating the query
        queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.all });
      }, delay);
    }
    
    // Show appropriate error message
    const errorMessage = error.message || SUPPORT_ERROR_MESSAGES[operation as keyof typeof SUPPORT_ERROR_MESSAGES] || 'An error occurred';
    toast({
      title: 'Error',
      description: errorMessage,
      variant: 'destructive'
    });
  }, [retryCount, queryClient]);

  // Create a new support request
  const createRequest = useMutation({
    mutationFn: async (data: CreateRequestData) => {
      if (!user || !userSchool) {
        throw new Error('User or school information not available');
      }

      const { data: request, error } = await supabase
        .from('support_requests')
        .insert({
          title: data.subject,
          description: data.description,
          request_type: 'support',
          category: data.category,
          priority: data.priority,
          requested_by: user.id,
          requester_name: user.user_metadata?.full_name || user.email || 'Unknown',
          requester_email: user.email || '',
          school_id: userSchool.id,
          school_name: userSchool.name,
          attachments: data.screenshots ? data.screenshots.map((url: string) => ({ type: 'image', url })) : [],
          metadata: data.metadata || {}
        })
        .select()
        .single();

      if (error) throw error;
      return request;
    },
    onMutate: async (newRequest) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: SUPPORT_QUERY_KEYS.requests(userSchool?.id || '') });

      // Snapshot the previous value
      const previousRequests = queryClient.getQueryData(SUPPORT_QUERY_KEYS.requests(userSchool?.id || ''));

      // Optimistically update to the new value
      queryClient.setQueryData(SUPPORT_QUERY_KEYS.requests(userSchool?.id || ''), (old: any) => {
        if (!old) return { data: [newRequest], count: 1 };
        return {
          ...old,
          data: [{ ...newRequest, id: 'temp-' + Date.now(), status: 'open' }, ...old.data],
          count: old.count + 1
        };
      });

      // Return a context object with the snapshotted value
      return { previousRequests };
    },
    onError: (err, newRequest, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousRequests) {
        queryClient.setQueryData(SUPPORT_QUERY_KEYS.requests(userSchool?.id || ''), context.previousRequests);
      }
      handleError(err, 'createRequest');
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: SUPPORT_SUCCESS_MESSAGES.createRequest,
        variant: 'default'
      });
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.requests(userSchool?.id || '') });
      setRetryCount(0);
    }
  });

  // Get paginated support requests
  const getRequests = (options: PaginationOptions & FilterOptions = {}) => {
    const {
      page = 1,
      pageSize = SUPPORT_PAGINATION.defaultPageSize,
      sortBy = 'created_at',
      sortOrder = 'desc',
      status,
      priority,
      category,
      searchTerm
    } = options;

    return useQuery({
      queryKey: [...SUPPORT_QUERY_KEYS.requests(userSchool?.id || ''), options],
      queryFn: async () => {
        if (!userSchool) {
          throw new Error('School information not available');
        }

        let query = supabase
          .from('support_requests')
          .select('*', { count: 'exact' })
          .eq('school_id', userSchool.id);

        // Apply filters
        if (status) query = query.eq('status', status);
        if (priority) query = query.eq('priority', priority);
        if (category) query = query.eq('category', category);
        if (searchTerm) {
          // SECURITY: Sanitize search input to prevent SQL injection
          const sanitizedSearchTerm = sanitizeSearchInput(searchTerm);
          if (sanitizedSearchTerm) {
            query = query.or(`title.ilike.%${sanitizedSearchTerm}%,description.ilike.%${sanitizedSearchTerm}%`);
          }
        }

        // Apply sorting
        query = query.order(sortBy, { ascending: sortOrder === 'asc' });

        // Apply pagination
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        query = query.range(from, to);

        const { data, error, count } = await query;

        if (error) throw error;

        return {
          data: data || [],
          count: count || 0,
          page,
          pageSize,
          totalPages: Math.ceil((count || 0) / pageSize)
        };
      },
      enabled: !!userSchool,
      staleTime: 30000, // 30 seconds
      retry: SUPPORT_RETRY_CONFIG.maxRetries,
      retryDelay: SUPPORT_RETRY_CONFIG.retryDelay
    });
  };

  // Get a single support request by ID
  const getRequestById = (requestId: string) => {
    return useQuery({
      queryKey: SUPPORT_QUERY_KEYS.request(requestId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('support_requests')
          .select('*')
          .eq('id', requestId)
          .single();

        if (error) throw error;
        return data;
      },
      enabled: !!requestId,
      staleTime: 30000,
      retry: SUPPORT_RETRY_CONFIG.maxRetries,
      retryDelay: SUPPORT_RETRY_CONFIG.retryDelay
    });
  };

  // Update a support request
  const updateRequest = useMutation({
    mutationFn: async ({ requestId, data }: { requestId: string; data: UpdateRequestData }) => {
      const { data: updated, error } = await supabase
        .from('support_requests')
        .update({
          ...data,
          updated_at: new Date().toISOString(),
          resolved_at: data.status === 'resolved' ? new Date().toISOString() : undefined
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return updated;
    },
    onMutate: async ({ requestId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: SUPPORT_QUERY_KEYS.request(requestId) });

      // Snapshot the previous value
      const previousRequest = queryClient.getQueryData(SUPPORT_QUERY_KEYS.request(requestId));

      // Optimistically update to the new value
      queryClient.setQueryData(SUPPORT_QUERY_KEYS.request(requestId), (old: any) => ({
        ...old,
        ...data,
        updated_at: new Date().toISOString()
      }));

      return { previousRequest };
    },
    onError: (err, { requestId }, context) => {
      // Roll back on error
      if (context?.previousRequest) {
        queryClient.setQueryData(SUPPORT_QUERY_KEYS.request(requestId), context.previousRequest);
      }
      handleError(err, 'updateRequest');
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: SUPPORT_SUCCESS_MESSAGES.updateRequest,
        variant: 'default'
      });
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.requests(userSchool?.id || '') });
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.request(data.id) });
      setRetryCount(0);
    }
  });

  // Add a comment to a support request
  const addComment = useMutation({
    mutationFn: async ({ requestId, content, isInternal = false }: CommentData) => {
      if (!user) {
        throw new Error('User information not available');
      }

      const { data: comment, error } = await supabase
        .from('support_request_comments')
        .insert({
          request_id: requestId,
          user_id: user.id,
          comment: content,
          user_name: user.user_metadata?.full_name || user.email || 'Unknown User',
          user_role: 'school_staff',
          is_internal: isInternal
        })
        .select('*')
        .single();

      if (error) throw error;

      // The trigger will automatically update the request's updated_at timestamp
      // No need to do it manually here

      return comment;
    },
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: SUPPORT_SUCCESS_MESSAGES.addComment,
        variant: 'default'
      });
      // Invalidate the request to refresh comments
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.request(data.request_id) });
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.comments(data.request_id) });
      setRetryCount(0);
    },
    onError: (err) => {
      handleError(err, 'addComment');
    }
  });

  // Get available support templates
  const getTemplates = (category?: SupportCategory) => {
    return useQuery({
      queryKey: SUPPORT_QUERY_KEYS.templates(category),
      queryFn: async () => {
        let query = supabase
          .from('support_request_templates')
          .select('*')
          .eq('is_active', true);

        if (category) {
          query = query.eq('category', category);
        }

        const { data, error } = await query.order('template_name');

        if (error) throw error;
        return data || [];
      },
      staleTime: 300000, // 5 minutes (templates don't change often)
      retry: SUPPORT_RETRY_CONFIG.maxRetries,
      retryDelay: SUPPORT_RETRY_CONFIG.retryDelay
    });
  };

  // Get comments for a support request
  const getComments = (requestId: string) => {
    return useQuery({
      queryKey: SUPPORT_QUERY_KEYS.comments(requestId),
      queryFn: async () => {
        const { data, error } = await supabase
          .from('support_request_comments')
          .select('*')
          .eq('request_id', requestId)
          .eq('is_internal', false) // School users only see non-internal comments
          .order('created_at', { ascending: true });

        if (error) throw error;
        return data || [];
      },
      enabled: !!requestId,
      staleTime: 30000,
      retry: SUPPORT_RETRY_CONFIG.maxRetries,
      retryDelay: SUPPORT_RETRY_CONFIG.retryDelay
    });
  };

  // Upload screenshots
  const uploadScreenshot = useCallback(async (file: File): Promise<string> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    const filePath = `support-screenshots/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('support-files')
      .upload(filePath, file);

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('support-files')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }, [user]);

  // Reset retry count on successful operations
  useEffect(() => {
    if (retryCount > 0) {
      const timer = setTimeout(() => setRetryCount(0), 60000); // Reset after 1 minute
      return () => clearTimeout(timer);
    }
  }, [retryCount]);

  return {
    // Operations
    createRequest,
    getRequests,
    getRequestById,
    updateRequest,
    addComment,
    getComments,
    getTemplates,
    uploadScreenshot,
    
    // State
    isAuthenticated: !!user && !!userSchool,
    userSchool,
    
    // Utilities
    refreshRequests: () => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.requests(userSchool?.id || '') });
    },
    clearCache: () => {
      queryClient.removeQueries({ queryKey: SUPPORT_QUERY_KEYS.all });
    }
  };
}